import { useMemo, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * Wellversed-branded data table with search + pagination.
 *
 *   <DataTable
 *     columns={[{ key, header, align, width, render }]}
 *     rows={[ { ... } ]}
 *     searchKeys={['sku', 'brand']}  // enables search box when provided
 *     searchPlaceholder="Search SKU or brand…"
 *     pageSizeOptions={[10, 15, 20, 100]}
 *     defaultPageSize={15}
 *     renderFilters={(helpers) => ...}  // optional extra filter ui
 *     renderFooter={(rows) => ...}       // optional totals row
 *     renderTopRow={(rows) => ...}       // optional totals row rendered below header, above data
 *     emptyMessage="No data"
 *     testId="my-table"
 *     stickyHeader
 *     maxHeight="520px"
 *   />
 */
export default function DataTable({
  columns,
  rows,
  searchKeys,
  searchPlaceholder = 'Search…',
  pageSizeOptions = [10, 15, 20, 100],
  defaultPageSize = 15,
  renderFilters,
  renderFooter,
  renderTopRow,
  emptyMessage = 'No data',
  testId,
  stickyHeader = true,
  maxHeight = '520px',
}) {
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(0);
  // Column sorting. `sortKey` matches a column with `sortable:true`.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey !== col.key) { setSortKey(col.key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    // asc → desc → clear
    setSortKey(null); setSortDir('asc');
  };

  const filtered = useMemo(() => {
    if (!searchKeys || !query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter(r =>
      searchKeys.some(k => {
        const v = r[k];
        return v !== undefined && v !== null && v.toString().toLowerCase().includes(q);
      })
    );
  }, [rows, searchKeys, query]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return filtered;
    const getter = col.sortAccessor || ((r) => r[sortKey]);
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = getter(a);
      const bv = getter(b);
      // Numeric vs string handling
      const isNumA = typeof av === 'number';
      const isNumB = typeof bv === 'number';
      if (isNumA && isNumB) return sortDir === 'asc' ? av - bv : bv - av;
      const sa = (av ?? '').toString().toLowerCase();
      const sb = (bv ?? '').toString().toLowerCase();
      if (sa < sb) return sortDir === 'asc' ? -1 : 1;
      if (sa > sb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir, columns]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page >= totalPages) setPage(0); }, [page, totalPages]);

  const start = page * pageSize;
  const pageRows = sorted.slice(start, start + pageSize);

  const renderFooterRows = renderFooter ? renderFooter(sorted) : null;
  const topRow = renderTopRow ? renderTopRow(sorted) : null;

  return (
    <div className="space-y-3" data-testid={testId}>
      {(searchKeys || renderFilters) && (
        <div className="flex flex-wrap items-center gap-3">
          {searchKeys && (
            <div className="relative w-full max-w-xs">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                placeholder={searchPlaceholder}
                className="h-9 pl-8 text-[14px]"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
          )}
          {renderFilters && (
            <div className="flex flex-wrap items-center gap-2">
              {renderFilters({ resetPage: () => setPage(0) })}
            </div>
          )}
          <div className="ml-auto text-xs text-[#64748B]" data-testid={testId ? `${testId}-count` : undefined}>
            {total.toLocaleString('en-IN')} row{total === 1 ? '' : 's'}
          </div>
        </div>
      )}

      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="overflow-auto" style={{ maxHeight }}>
          <table className="w-full" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            <thead className={`${stickyHeader ? 'sticky top-0 z-10' : ''} bg-[#2EB4AC]`}>
              <tr>
                {columns.map((c) => {
                  const isActive = sortKey === c.key;
                  const SortIcon = !c.sortable ? null : isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c)}
                      className={`px-3 py-3 text-[11px] font-semibold tracking-[0.1em] uppercase text-white/95 ${c.sortable ? 'cursor-pointer select-none hover:text-white' : ''}`}
                      style={{
                        textAlign: c.align || 'left',
                        width: c.width,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                        {c.header}
                        {SortIcon && <SortIcon size={12} className={isActive ? 'text-white' : 'text-white/60'} />}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {topRow && (
                <tr
                  className="bg-[#0F0F0F] text-white font-semibold sticky top-[44px] z-[9]"
                  data-testid={testId ? `${testId}-totals-row` : undefined}
                >
                  {topRow}
                </tr>
              )}
              {pageRows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-6 text-center text-[14px] text-[#64748B]"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {pageRows.map((row, idx) => (
                <tr
                  key={row.__key ?? idx}
                  className="bg-white border-b border-[#F1F5F9] last:border-b-0 hover:bg-[#ECFDF9] transition-colors"
                  data-testid={testId ? `${testId}-row-${start + idx}` : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className="px-3 py-3 text-[13px] text-[#1A1A1A]"
                      style={{ textAlign: c.align || 'left', whiteSpace: c.nowrap ? 'nowrap' : undefined }}
                    >
                      {c.render ? c.render(row, start + idx) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {renderFooterRows && (
              <tfoot className="bg-[#0F0F0F] text-white sticky bottom-0">
                {renderFooterRows}
              </tfoot>
            )}
          </table>
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t border-[#E2E8F0] bg-white text-[12px] text-[#64748B]">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              className="h-7 px-2 rounded border border-[#E2E8F0] bg-white text-[12px] text-[#334155] focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/40"
              data-testid={testId ? `${testId}-page-size` : undefined}
            >
              {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              {total === 0
                ? '0 of 0'
                : `${(start + 1).toLocaleString('en-IN')}–${Math.min(start + pageSize, total).toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`}
            </span>
            <div className="flex items-center">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                data-testid={testId ? `${testId}-prev` : undefined}
                className="h-7 w-7 inline-flex items-center justify-center rounded text-[#64748B] hover:text-[#2EB4AC] hover:bg-[#F8FAFC] disabled:opacity-40 disabled:hover:text-[#64748B] disabled:hover:bg-transparent transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-[12px] text-[#334155] tabular-nums min-w-[48px] text-center">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                data-testid={testId ? `${testId}-next` : undefined}
                className="h-7 w-7 inline-flex items-center justify-center rounded text-[#64748B] hover:text-[#2EB4AC] hover:bg-[#F8FAFC] disabled:opacity-40 disabled:hover:text-[#64748B] disabled:hover:bg-transparent transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
